import React, { useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar as CalendarIcon, Show as ShowIcon, Hide as HideIcon } from "../normalizer";

registerLocale("pt", pt);

/* ── Portuguese month names (capitalised) ── */
const PT_MONTHS = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];

/* ─────────────────────────────────────────────────────────────────
   Shared helpers
───────────────────────────────────────────────────────────────── */

const InputTitle = ({ title }) =>
  title ? <div className="title">{title}</div> : null;

const InputHint = ({ hint }) =>
  hint ? (
    <div className="hint-container">
      <p className="hint">{hint.join(" ")}</p>
    </div>
  ) : null;

const InputError = ({ error }) =>
  error ? (
    <div className="error-container">
      <p className="error">{error.join(" ")}</p>
    </div>
  ) : null;

/* ─────────────────────────────────────────────────────────────────
   DatePickerHeader
───────────────────────────────────────────────────────────────── */

const DatePickerHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <div className="gl-datepicker__header">
    <button
      type="button"
      className="gl-datepicker__nav gl-datepicker__nav--prev"
      onClick={decreaseMonth}
      disabled={prevMonthButtonDisabled}
      aria-label="Mês anterior"
    >
      ‹
    </button>
    <span className="gl-datepicker__month-year">
      {PT_MONTHS[date.getMonth()]} {date.getFullYear()}
    </span>
    <button
      type="button"
      className="gl-datepicker__nav gl-datepicker__nav--next"
      onClick={increaseMonth}
      disabled={nextMonthButtonDisabled}
      aria-label="Próximo mês"
    >
      ›
    </button>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   DateInput  (date / datetime-local / time)
───────────────────────────────────────────────────────────────── */

const DateInput = ({ title, error, hint, leftIcon, rightIcon, disabled, onChange, className, ...rest }) => (
  <div className="gl-input">
    <InputTitle title={title} />
    <div className={`input-container input-container--date ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}>
      {leftIcon && <span className="input-icon">{leftIcon}</span>}
      <DatePicker
        selected={rest.value || null}
        onChange={onChange}
        dateFormat="dd/MM/yyyy"
        locale="pt"
        placeholderText={rest.placeholder || "dd/mm/aaaa"}
        disabled={disabled}
        name={rest.name}
        minDate={rest.minDate}
        maxDate={rest.maxDate}
        calendarClassName="gl-datepicker"
        className={`input ${className ? className : ""}`}
        renderCustomHeader={DatePickerHeader}
        showDisabledMonthNavigation
      />
      <span className="input-icon">{rightIcon || <CalendarIcon />}</span>
    </div>
    <InputHint hint={hint} />
    <InputError error={error} />
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   PasswordInput  (password with show/hide toggle)
───────────────────────────────────────────────────────────────── */

const PasswordInput = ({ title, error, hint, leftIcon, disabled, onChange, className, ...rest }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="gl-input">
      <InputTitle title={title} />
      <div className={`input-container input-container--password ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}>
        {leftIcon && <span className="input-icon">{leftIcon}</span>}
        <input
          type={showPassword ? "text" : "password"}
          className={`input ${className ? className : ""}`}
          disabled={disabled ? disabled : null}
          onChange={onChange ? onChange : null}
          {...rest}
        />
        <button
          type="button"
          className="input-icon input-icon--toggle"
          onClick={() => setShowPassword((prev) => !prev)}
          tabIndex={-1}
          aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
        >
          {showPassword ? <HideIcon /> : <ShowIcon />}
        </button>
      </div>
      <InputHint hint={hint} />
      <InputError error={error} />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TextInput  (all other types)
───────────────────────────────────────────────────────────────── */

const TextInput = ({ title, error, hint, leftIcon, rightIcon, disabled, readOnly, onChange, className, type, ...rest }) => (
  <div className="gl-input">
    <InputTitle title={title} />
    <div className={`input-container input-container--${type} ${error ? "error" : ""} ${(disabled || readOnly) ? "disabled" : ""}`}>
      {leftIcon && <span className="input-icon">{leftIcon}</span>}
      <input
        type={type}
        className={`input ${className ? className : ""}`}
        disabled={disabled ? disabled : null}
        readOnly={readOnly ? readOnly : null}
        onChange={onChange ? onChange : null}
        {...rest}
      />
      {rightIcon && <span className="input-icon">{rightIcon}</span>}
    </div>
    <InputHint hint={hint} />
    <InputError error={error} />
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   InputKeyValue  (router)
───────────────────────────────────────────────────────────────── */

const DATE_TYPES = ["date", "datetime-local", "time"];

const InputKeyValue = ({ type = "text", ...props }) => {
  if (DATE_TYPES.includes(type))  return <DateInput     {...props} />;
  if (type === "password")        return <PasswordInput {...props} />;
                                  return <TextInput     {...props} type={type} />;
};

export default InputKeyValue;
